import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Download, Loader2, RotateCcw } from 'lucide-react';
import { lmError } from './lmApi';
import { StateMessage } from './LmBits';

/**
 * For Pooling → Rep Distribution and Rep Balance (prototype `subRepDist`,
 * `subRepBal`). Real data: CRM owner counts plus pooled rows by owner.
 * Applying a balance writes ONLY the pooled rows' owner — no CRM write.
 */
// Avatar chip per rep: tinted background + readable text, each with its Graphite pair.
const PALETTE = [
    'bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300',
    'bg-cyan-100 text-cyan-800 dark:bg-cyan-500/20 dark:text-cyan-300',
    'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300',
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
    'bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-300',
    'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300',
];
// Bar fills (need >= 3:1 against the bg-muted track in both themes).
const BAR_BRAND = 'bg-indigo-600 dark:bg-indigo-400';
const BAR_UNASSIGNED = 'bg-amber-700 dark:bg-warn';
const BAR_OVER = 'bg-red-600 dark:bg-bad';
// Over / Even / Under badges.
const BADGE_OVER = 'bg-red-50 text-red-800 dark:bg-bad-bg dark:text-bad';
const BADGE_EVEN = 'bg-muted text-ink-2';
const BADGE_UNDER = 'bg-emerald-50 text-emerald-800 dark:bg-good-bg dark:text-good';

function useLoad(fn, deps) {
    const [state, setState] = useState({ loading: true, data: null, error: null });
    const reload = useCallback(() => {
        setState((s) => ({ ...s, loading: true, error: null }));
        fn().then((data) => setState({ loading: false, data, error: null }))
            .catch((err) => setState({ loading: false, data: null, error: lmError(err, 'Could not load this view.') }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    useEffect(() => { reload(); }, [reload]);
    return { ...state, reload };
}

/** One quoted CSV cell. A leading = + - @ (or tab/CR) would be evaluated as a
 *  formula by Excel/Sheets — a rep could name themselves `=HYPERLINK(...)` — so
 *  such cells are prefixed with an apostrophe. Quoting alone does not stop it. */
export function csvCell(v) {
    let text = String(v ?? '');
    if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
    return `"${text.replace(/"/g, '""')}"`;
}

function exportCsv(reps) {
    const header = ['Sales Rep', 'CRM Companies', 'CRM Contacts', 'Pending Companies', 'Pending Contacts', 'Total Companies', 'Total Contacts', 'Share %'];
    const lines = [header, ...reps.map((r) => [r.name, r.crm_companies, r.crm_contacts, r.pending_companies, r.pending_contacts,
                                               r.total_companies, r.total_contacts, r.share])];
    const csv = lines.map((l) => l.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'rep-distribution.csv'; a.click();
    URL.revokeObjectURL(url);
}

export function RepDistribution({ api, refreshKey }) {
    const { loading, data, error, reload } = useLoad(() => api.repDistribution(), [api, refreshKey]);
    const reps = data?.reps || [];
    return (
        <div data-testid="lm-rep-distribution">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className="text-[17px] font-semibold tracking-[-0.015em] text-foreground">Rep Distribution</div>
                    <div className="mt-1 text-[13px] text-muted-foreground">Records per rep in the CRM today, plus what the pending batches will add.</div>
                </div>
                <button type="button" onClick={() => exportCsv(reps)} disabled={!reps.length}
                        className="inline-flex items-center gap-2 h-9 px-[13px] border border-input rounded-[9px] bg-card text-[13px] font-medium text-foreground hover:bg-accent disabled:opacity-45">
                    <Download className="w-[15px] h-[15px] text-muted-foreground" aria-hidden="true" />Export
                </button>
            </div>
            <div className="mt-4 bg-card border border-border rounded-xl overflow-x-auto">
                {error ? <StateMessage kind="error" title="Could not load the distribution" detail={error}
                                       action={<button type="button" className="text-[13px] text-indigo-700 dark:text-indigo-300" onClick={reload}>Try again</button>} />
                    : loading ? <div className="py-12 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                    : (
                        <table className="border-collapse w-full min-w-[1100px]">
                            <thead>
                                <tr className="bg-muted/50">
                                    <th rowSpan={2} scope="col" className="px-[18px] py-3 text-left text-[11px] font-semibold tracking-[0.06em] text-muted-foreground whitespace-nowrap">SALES REP</th>
                                    {['IN CRM', '+ PENDING', 'TOTAL AFTER'].map((g) => (
                                        <th key={g} colSpan={2} scope="colgroup" className="px-2.5 pt-3 pb-1 text-center text-[11px] font-semibold tracking-[0.06em] text-muted-foreground">{g}</th>
                                    ))}
                                    <th rowSpan={2} scope="col" className="px-[18px] py-3 text-left text-[11px] font-semibold tracking-[0.06em] text-muted-foreground">BALANCE</th>
                                </tr>
                                <tr className="bg-muted/50">
                                    {['Companies', 'Contacts', 'Companies', 'Contacts', 'Companies', 'Contacts'].map((c, i) => (
                                        <th key={i} scope="col" className="px-2.5 pb-2.5 text-center text-[11.5px] font-normal text-muted-foreground whitespace-nowrap">{c}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {reps.map((r) => (
                                    <tr key={r.user_id || 'unassigned'} className="border-t border-border/60 hover:bg-muted/50">
                                        <td className="px-[18px] py-3 text-[13.5px] text-foreground whitespace-nowrap">{r.name}</td>
                                        <td className="px-2.5 py-3 text-center text-[13px] text-ink-2 tabular-nums">{r.crm_companies}</td>
                                        <td className="px-2.5 py-3 text-center text-[13px] text-ink-2 tabular-nums">{r.crm_contacts}</td>
                                        <td className="px-2.5 py-3 text-center text-[13px] text-cyan-700 dark:text-cyan-300 tabular-nums">{r.pending_companies}</td>
                                        <td className="px-2.5 py-3 text-center text-[13px] text-cyan-700 dark:text-cyan-300 tabular-nums">{r.pending_contacts}</td>
                                        <td className="px-2.5 py-3 text-center text-[13px] font-semibold text-foreground tabular-nums">{r.total_companies}</td>
                                        <td className="px-2.5 py-3 text-center text-[13px] font-semibold text-foreground tabular-nums">{r.total_contacts}</td>
                                        <td className="px-[18px] py-3">
                                            <div className="flex items-center gap-2.5">
                                                <div className="flex-1 min-w-[120px] h-[7px] rounded-full bg-muted overflow-hidden">
                                                    <div className={`h-full rounded-full ${r.user_id ? BAR_BRAND : BAR_UNASSIGNED}`} style={{ width: `${r.share}%` }} />
                                                </div>
                                                <span className="shrink-0 min-w-[32px] text-right text-xs text-muted-foreground tabular-nums">{r.share}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </div>
        </div>
    );
}

export function RepBalance({ api, refreshKey, canAssign, onApplied }) {
    const dist = useLoad(() => api.repDistribution(), [api, refreshKey]);
    const [suggestion, setSuggestion] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const suggest = useCallback(async () => {
        setBusy(true); setError(null);
        try { setSuggestion(await api.repBalanceSuggest()); } catch (err) { setError(lmError(err, 'No suggestion could be made.')); setSuggestion(null); } finally { setBusy(false); }
    }, [api]);

    useEffect(() => { suggest(); }, [suggest, refreshKey]);

    const apply = async () => {
        if (!suggestion?.proposals?.length) return;
        setBusy(true); setError(null);
        try {
            const res = await api.bulk('assign-bulk', { assignments: suggestion.proposals.map((p) => ({ row_id: p.row_id, owner_id: p.owner_id })) });
            if (res.failed) toast.warning(`${res.succeeded} assigned · ${res.failed} could not be assigned`);
            else toast.success(`${res.succeeded} pooled record${res.succeeded === 1 ? '' : 's'} assigned`);
            onApplied?.();
            dist.reload();
            await suggest();
        } catch (err) {
            setError(lmError(err, 'The assignment could not be applied.'));
        } finally {
            setBusy(false);
        }
    };

    const reps = (dist.data?.reps || []).filter((r) => r.user_id);
    const unassigned = (dist.data?.reps || []).find((r) => !r.user_id);
    const projected = Object.fromEntries((suggestion?.projected || []).map((p) => [p.user_id, p]));
    const target = suggestion?.target_per_rep ?? 0;
    const rows = reps.map((r, i) => {
        const p = projected[r.user_id];
        const after = p ? p.load_after : r.total_contacts;
        const delta = after - target;
        return { ...r, after, delta, color: PALETTE[i % PALETTE.length] };
    });
    const over = rows.filter((r) => r.delta > 0).length;

    return (
        <div data-testid="lm-rep-balance">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className="text-[17px] font-semibold tracking-[-0.015em] text-foreground">Rep Balance</div>
                    <div className="mt-1 text-[13px] text-muted-foreground">
                        {(unassigned?.pending_contacts || 0) + rows.reduce((n, r) => n + r.pending_contacts, 0)} pending contacts across {rows.length} reps
                        · {suggestion?.unassigned_rows ?? 0} unassigned need an owner before commit.
                    </div>
                </div>
                <button type="button" onClick={suggest} disabled={busy}
                        className="inline-flex items-center gap-2 h-9 px-[13px] border border-input rounded-[9px] bg-card text-[13px] font-medium text-foreground hover:bg-accent">
                    <RotateCcw className="w-[15px] h-[15px] text-muted-foreground" aria-hidden="true" />Reset
                </button>
                {canAssign && (
                    <button type="button" onClick={apply} disabled={busy || !suggestion?.proposals?.length} data-testid="lm-apply-balance"
                            className="inline-flex items-center gap-2 h-9 px-[15px] bg-primary hover:bg-primary/90 text-primary-foreground rounded-[9px] text-[13px] font-semibold disabled:opacity-40">
                        <CheckCircle2 className="w-[15px] h-[15px]" aria-hidden="true" />Apply assignment
                    </button>
                )}
            </div>
            {error && <div role="alert" className="mt-3 px-3.5 py-2.5 rounded-[10px] bg-red-50 dark:bg-bad-bg text-[12.5px] text-red-800 dark:text-bad">{error}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mt-4">
                {[['UNASSIGNED', suggestion?.unassigned_rows ?? 0, 'Pending records with no owner', true],
                  ['OVER TARGET', over, 'Reps above the even split', false],
                  ['UNDER TARGET', rows.length - over, 'Reps with room for more', false]].map(([l, val, note, warn]) => (
                    <div key={l} className={`rounded-xl px-[18px] py-4 border ${warn ? 'bg-amber-50 dark:bg-warn-bg border-amber-200 dark:border-warn-bg' : 'bg-card border-border'}`}>
                        <div className={`text-[11px] font-semibold tracking-[0.06em] ${warn ? 'text-ink-2' : 'text-muted-foreground'}`}>{l}</div>
                        <div className="mt-2 text-2xl font-semibold tracking-[-0.02em] text-foreground tabular-nums">{val}</div>
                        <div className={`mt-1 text-xs ${warn ? 'text-ink-2' : 'text-muted-foreground'}`}>{note}</div>
                    </div>
                ))}
            </div>
            <div className="mt-4 bg-card border border-border rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 px-[18px] py-3.5 border-b border-border">
                    <span className="flex-1 text-sm font-semibold text-foreground">Load after commit</span>
                    <span className="text-[12.5px] text-muted-foreground tabular-nums">Target {target} contacts per rep</span>
                </div>
                {dist.loading && <div className="py-10 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>}
                {rows.map((r) => {
                    const pct = Math.min(100, Math.round((r.after / ((target || 1) * 1.6)) * 100));
                    const badge = r.delta > 0 ? ['Over', BADGE_OVER] : r.delta === 0 ? ['Even', BADGE_EVEN] : ['Under', BADGE_UNDER];
                    const initials = (r.name || '').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
                    const gain = (suggestion?.proposals || []).filter((p) => p.owner_id === r.user_id).length;
                    return (
                        <div key={r.user_id} className="flex items-center gap-4 px-[18px] py-[13px] border-b border-border/60">
                            <span className={`shrink-0 inline-flex items-center justify-center w-[30px] h-[30px] rounded-full text-[11px] font-semibold ${r.color}`}>{initials}</span>
                            <div className="w-[190px] shrink-0 min-w-0">
                                <div className="text-[13.5px] font-medium text-foreground truncate">{r.name}</div>
                                <div className="text-xs text-muted-foreground mt-0.5 tabular-nums">{r.after} contacts · {r.total_companies} companies{gain ? ` · +${gain} from this suggestion` : ''}</div>
                            </div>
                            <div className="flex-1 min-w-[120px] h-2 rounded-full bg-muted overflow-hidden">
                                <div className={`h-full rounded-full ${r.delta > 0 ? BAR_OVER : BAR_BRAND}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className={`shrink-0 inline-flex items-center h-[22px] px-2 rounded-md text-[11.5px] font-semibold ${badge[1]}`}>{badge[0]}</span>
                            <span className="w-[90px] shrink-0 text-right text-[13px] font-semibold text-foreground tabular-nums" title="Contacts above (+) or below (−) the even split after commit">
                                {r.delta > 0 ? `+${r.delta}` : String(r.delta)}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
